import React, { useState, useEffect } from "react";
import { MdPreview } from "react-icons/md";
import Swal from "sweetalert2";
import { ImageUpload_API, BlogContent_API } from "./environment";
import "./blogContentEditor.css";

/**
 * BlogContentEditor (updated)
 * - Sanitizes template names so generated JSX is valid
 * - Injects actual form data into generated code (tabs/contentData)
 * - Keeps previous behavior for image upload and schema-driven UI
 *
 * Props:
 * - chapterName (string)
 * - templates (array)
 * - formData (object)
 * - setFormData (fn)
 * - selectedTemplate (string|number)
 * - setSelectedTemplate (fn)
 * - editBlog (object)
 * - userData (object)
 * - onSaved (fn)
 */

// Sanitize template name to be JSX-safe: remove spaces and special characters
function sanitizeTemplateName(name = "") {
  if (!name) return "Template";
  // If template names sometimes contain parentheses or descriptions like "ComparisonTemplate(Pros & Cons)"
  // we strip everything except alphanumerics and underscore
  const m = String(name).match(/[A-Za-z0-9_]+/g);
  if (!m || m.length === 0) return "Template";
  // Prefer the first token that looks like a component name
  return m[0];
}

export default function BlogContentEditor(props) {
  const {
    chapterName,
    templates,
    formData,
    setFormData,
    selectedTemplate,
    setSelectedTemplate,
    editBlog,
    userData,
    onSaved,
    savedChapters,
    currentChapterIndex,
  } = props;

  const [localTemplateSchema, setLocalTemplateSchema] = useState(null);
  const [localTemplateObj, setLocalTemplateObj] = useState(null);
  const [showPreviewUrl, setShowPreviewUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  // track existing saved entry (same blog & chapter)
  const [existingSavedEntry, setExistingSavedEntry] = useState(null);
  const [isDeletingExisting, setIsDeletingExisting] = useState(false);

  // When selectedTemplate changes -> set schema
  useEffect(() => {
    if (!selectedTemplate) {
      setLocalTemplateSchema(null);
      setLocalTemplateObj(null);
      setIsSaved(false);
      return;
    }
    const tpl = templates.find(
      (t) => parseInt(t.template_id) === parseInt(selectedTemplate)
    );
    if (tpl) {
      // template_schema may be JSON string or object
      let schema = tpl.template_schema || null;
      if (typeof schema === "string") {
        try {
          schema = JSON.parse(schema);
        } catch (err) {
          console.error(
            "Failed parse schema for template",
            tpl.template_id,
            err
          );
          schema = null;
        }
      }
      setLocalTemplateSchema(schema);
      setLocalTemplateObj(tpl);

      // initialize commonly used arrays in formData (if not present)
      if (schema && typeof schema === "object") {
        Object.keys(schema).forEach((k) => {
          if (Array.isArray(schema[k]) && !formData[k]) {
            updateField(k, []);
          }
          if (schema[k] === "string" && formData[k] === undefined) {
            updateField(k, "");
          }
        });
      }
    } else {
      setLocalTemplateSchema(null);
      setLocalTemplateObj(null);
    }
    setIsSaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, templates]);

  // Load saved chapter data whenever chapterName or savedChapters changes.
  // Also, detect an existing DB entry for same blog_id + chapter and prompt user.
  useEffect(() => {
    // reset if no chapters or no chapterName
    if (!savedChapters || savedChapters.length === 0 || !chapterName) {
      setFormData({});
      setSelectedTemplate("");
      setExistingSavedEntry(null);
      return;
    }

    // find entry matching both chapter name AND blog id
    const savedEntry = savedChapters.find(
      (c) =>
        String(c.chapter_name).trim() === String(chapterName).trim() &&
        // some older data may not include blog_id; guard it
        c.blog_id &&
        editBlog &&
        editBlog.blog_id &&
        parseInt(c.blog_id) === parseInt(editBlog.blog_id)
    );

    if (!savedEntry) {
      // no existing DB entry → fall back to previous parsing behavior (clear or load nothing)
      setFormData({});
      setSelectedTemplate("");
      setExistingSavedEntry(null);
      return;
    }

    // If we reach here: there is an existing DB entry for this blog + chapter.
    setExistingSavedEntry(savedEntry);

    // Show prompt to user: Keep existing & move next, or Delete & add new
    Swal.fire({
      title: "Existing chapter found",
      icon: "warning",
      html:
        "<p>This chapter already has saved content in database.</p>" +
        "<p>Do you want to keep the existing content and move to the next chapter, or delete the old content and add new?</p>",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Keep & Move to Next",
      denyButtonText: "Delete & Add New",
      cancelButtonText: "Cancel (Open for editing)",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        // Keep existing content — do not modify DB — move to next chapter
        // Optionally load the savedEntry into formData for visual confirmation — but requirement says skip editing and move on
        if (typeof props.onNext === "function") {
          props.onNext();
        } else {
          // fallback: load the saved data (non-editable) so user sees it
          setFormData({}); // keep UI clean
          setSelectedTemplate(savedEntry.template_id);
        }
      } else if (result.isDenied) {
        // User chose to delete old content and add new — call delete API then clear form
        try {
          setIsDeletingExisting(true);
          const delResp = await fetch(
            `${BlogContent_API}/delete/${savedEntry.id}`,
            {
              method: "DELETE",
              headers: {
                Authorization:
                  "Bearer " + (userData ? userData.access_token : ""),
                "Content-Type": "application/json",
              },
            }
          );
          if (!delResp.ok) {
            const txt = await delResp.text();
            throw new Error(txt || "Delete failed");
          }
          // remove from local UI: clear form and template so user can add new content
          setFormData({});
          setSelectedTemplate("");
          setExistingSavedEntry(null);

          // inform parent (optional) so parent can re-fetch savedChaptersData
          if (typeof props.onSaved === "function") {
            props.onSaved({ deletedId: savedEntry.id });
          }

          Swal.fire(
            "Deleted",
            "Old chapter removed. You can add new content now.",
            "success"
          );
        } catch (err) {
          console.error("Delete existing chapter error:", err);
          Swal.fire(
            "Error",
            "Failed to delete existing chapter. Try again.",
            "error"
          );
        } finally {
          setIsDeletingExisting(false);
        }
      } else {
        // Cancel: user wants to open and edit — load the existing content into the form for editing (read-only edit not allowed by requirements, but we'll load)
        // We'll reuse your old parsing logic to load the savedEntry code props into formData
        try {
          const codeStr = savedEntry.code || "";
          const tagMatch = codeStr.match(/<([A-Za-z0-9_]+)\s+([^>]+)\/>/);
          if (tagMatch) {
            const propsStr = tagMatch[2];
            const tempData = {};
            let parsedAnything = false;

            propsStr.replace(/(\w+)=\{([^}]*)\}/g, (match, key, value) => {
              try {
                const trimmed = value.trim();
                tempData[key] =
                  trimmed.startsWith("[") || trimmed.startsWith("{")
                    ? JSON.parse(trimmed)
                    : trimmed;
                parsedAnything = true;
              } catch (e) {}
              return "";
            });

            propsStr.replace(/(\w+)=\[([^\]]*)\]/g, (match, key, inner) => {
              try {
                tempData[key] = JSON.parse(`[${inner}]`);
                parsedAnything = true;
              } catch (e) {}
              return "";
            });

            propsStr.replace(/(\w+)="([^"]*)"/g, (match, key, value) => {
              tempData[key] = value;
              parsedAnything = true;
              return "";
            });

            if (parsedAnything && Object.keys(tempData).length > 0) {
              setFormData(tempData);
              setSelectedTemplate(savedEntry.template_id);
              return;
            }
          }

          // fallback: nothing parsed — keep clean for safety
          setFormData({});
          setSelectedTemplate("");
        } catch (err) {
          setFormData({});
          setSelectedTemplate("");
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterName, savedChapters]);

  // -------------------------
  // Helpers: update form data
  // -------------------------
  function updateField(name, value) {
    setFormData((prev) => {
      return Object.assign({}, prev, { [name]: value });
    });
    setIsSaved(false);
  }

  function addArrayItem(field, defaultValue) {
    setFormData((prev) => {
      const arr = Array.isArray(prev[field]) ? prev[field].slice() : [];
      arr.push(defaultValue);
      return Object.assign({}, prev, { [field]: arr });
    });
    setIsSaved(false);
  }

  function updateArrayItem(field, index, key, value) {
    setFormData((prev) => {
      const arr = (prev[field] || []).slice();
      const item = Object.assign({}, arr[index] || {});
      item[key] = value;
      arr[index] = item;
      return Object.assign({}, prev, { [field]: arr });
    });
    setIsSaved(false);
  }

  function removeArrayItem(field, index) {
    setFormData((prev) => {
      const arr = (prev[field] || []).slice();
      arr.splice(index, 1);
      return Object.assign({}, prev, { [field]: arr });
    });
    setIsSaved(false);
  }

  // -------------------------
  // Image upload helper
  // -------------------------
  async function uploadImageFile(file) {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const resp = await fetch(ImageUpload_API, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + (userData ? userData.access_token : ""),
        },
        body: fd,
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || "Upload failed");
      }
      const json = await resp.json();
      // Expect json.path (or json.data.path depending on API)
      return json.path || (json.data && json.data.path) || null;
    } catch (err) {
      console.error("Image upload error:", err);
      Swal.fire("Error", "Image upload failed. Try again.", "error");
      return null;
    }
  }

  // -------------------------
  // Render helpers for schema-driven UI
  // -------------------------
  function renderFieldByType(fieldKey, def) {
    if (def === "string") {
      const keyLower = fieldKey.toLowerCase();
      const isImageField =
        keyLower.includes("imageurl") ||
        keyLower.includes("imgurl") ||
        keyLower.includes("image_path") ||
        keyLower.includes("imagepath");

      // If field looks like an image URL → show image upload UI
      if (isImageField) {
        return (
          <div className="bce-field-box" key={fieldKey}>
            <label className="bce-label">{fieldKey} (Image)</label>

            <input
              type="file"
              accept="image/*"
              className="bce-input-file"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;

                const path = await uploadImageFile(file);
                if (path) {
                  updateField(fieldKey, path);
                }
              }}
            />

            {/* Show uploaded URL */}
            {formData[fieldKey] && (
              <p className="bce-file-name">{formData[fieldKey]}</p>
            )}
          </div>
        );
      }
      // Default: simple text input
      return (
        <div className="bce-field-box" key={fieldKey}>
          <label className="bce-label">{fieldKey}</label>
          <input
            className="bce-input"
            type="text"
            value={formData[fieldKey] || ""}
            onChange={(e) => {
              updateField(fieldKey, e.target.value);
            }}
          />
        </div>
      );
    }

    // CASE: array of strings → simple list input (e.g., leftPros, rightCons)
    if (Array.isArray(def) && def.length > 0 && typeof def[0] === "string") {
      return (
        <div className="bce-field-box" key={fieldKey}>
          <label className="bce-label">{fieldKey}</label>

          {(formData[fieldKey] || []).map((item, idx) => (
            <div className="bce-sub-field-box" key={idx}>
              <input
                className="bce-input"
                type="text"
                value={item || ""}
                onChange={(e) => {
                  const arr = [...(formData[fieldKey] || [])];
                  arr[idx] = e.target.value;
                  updateField(fieldKey, arr);
                }}
              />
              <button
                className="bce-remove-btn"
                onClick={() => {
                  const arr = [...(formData[fieldKey] || [])];
                  arr.splice(idx, 1);
                  updateField(fieldKey, arr);
                }}
              >
                ✕
              </button>
            </div>
          ))}

          <button
            className="bce-add-btn"
            onClick={() => {
              const arr = [...(formData[fieldKey] || [])];
              arr.push("");
              updateField(fieldKey, arr);
            }}
          >
            + Add {fieldKey}
          </button>
        </div>
      );
    }
    // CASE: array of objects

    if (Array.isArray(def) && def.length > 0 && typeof def[0] === "object") {
      // SPECIAL CASE: TemplateNew → custom UI
      if (localTemplateObj?.template_id == 9 && fieldKey === "contentData") {
        return (
          <div className="bce-field-box" key={fieldKey}>
            <label>{fieldKey}</label>

            {(formData[fieldKey] || []).map((item, idx) =>
              renderTemplateNewContentData(fieldKey, item, idx)
            )}

            <button
              className="bce-add-btn"
              onClick={() => {
                addArrayItem(fieldKey, {
                  text: "",
                  type: "image",
                  title: "",
                  source: "",
                  frameUrl: "",
                  frameTitle: "",
                });
              }}
            >
              + Add {fieldKey}
            </button>
          </div>
        );
      }
      const itemSchema = def[0];
      return (
        <div className="bce-field-box" key={fieldKey}>
          <label className="bce-label">{fieldKey}</label>

          <div className="bce-array-object-wrapper">
            {(formData[fieldKey] || []).map((item, idx) => {
              return (
                <div className="bce-array-object-box" key={idx}>
                  <div className="bce-array-object-header">
                    <span className="bce-array-object-title">
                      {fieldKey} #{idx + 1}
                    </span>
                    <button
                      className="bce-remove-btn"
                      onClick={() => {
                        removeArrayItem(fieldKey, idx);
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {Object.keys(itemSchema).map((k) => {
                    const t = itemSchema[k];
                    if (t === "string") {
                      return (
                        <div className="bce-sub-field-box" key={k}>
                          <label className="bce-label">{k}</label>
                          <input
                            className="bce-input-tab"
                            type="text"
                            value={
                              (formData[fieldKey] &&
                                formData[fieldKey][idx] &&
                                formData[fieldKey][idx][k]) ||
                              ""
                            }
                            onChange={(e) => {
                              updateArrayItem(fieldKey, idx, k, e.target.value);
                            }}
                          />
                        </div>
                      );
                    }

                    if (typeof t === "string" && t.indexOf("select:") === 0) {
                      const opts = t.replace("select:", "").split(",");
                      return (
                        <div className="bce-sub-field-box" key={k}>
                          <label className="bce-label">{k}</label>
                          <select
                            className="bce-select-type"
                            value={
                              (formData[fieldKey] &&
                                formData[fieldKey][idx] &&
                                formData[fieldKey][idx][k]) ||
                              opts[0]
                            }
                            onChange={(e) => {
                              updateArrayItem(fieldKey, idx, k, e.target.value);
                            }}
                          >
                            {opts.map((opt) => (
                              <option value={opt} key={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    }

                    if (t === "image") {
                      return (
                        <div className="bce-sub-field-box" key={k}>
                          <label className="bce-label">{k}</label>
                          <input
                            type="file"
                            accept="image/*"
                            className="bce-input-file"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              const path = await uploadImageFile(file);
                              if (path) {
                                updateArrayItem(fieldKey, idx, k, path);
                              }
                            }}
                          />
                          {formData[fieldKey] &&
                            formData[fieldKey][idx] &&
                            formData[fieldKey][idx][k] && (
                              <p className="bce-file-name">
                                {formData[fieldKey][idx][k]}
                              </p>
                            )}
                        </div>
                      );
                    }

                    return null;
                  })}
                </div>
              );
            })}
          </div>

          <button
            className="bce-add-btn"
            onClick={() => {
              const defObj = {};
              Object.keys(itemSchema).forEach((k) => {
                const t = itemSchema[k];
                if (t === "string") defObj[k] = "";
                else if (typeof t === "string" && t.indexOf("select:") === 0)
                  defObj[k] = t.replace("select:", "").split(",")[0];
                else defObj[k] = "";
              });
              addArrayItem(fieldKey, defObj);
            }}
          >
            + Add {fieldKey}
          </button>
        </div>
      );
    }

    if (typeof def === "object") {
      return Object.keys(def).map((k) => renderFieldByType(k, def[k]));
    }

    return null;
  }

  // -------------------------
  // Save handler (construct payload and POST)
  // -------------------------
  async function handleSave() {
    if (!selectedTemplate) {
      Swal.fire("Error", "Please select a template first.", "error");
      return;
    }

    // Pre-check: prevent duplicate entry unless user deletes old one
    if (existingSavedEntry && existingSavedEntry.id) {
      const res = await Swal.fire({
        title: "Existing saved content detected",
        text: "This chapter already exists in database. To overwrite, delete the old entry first. Do you want to delete old entry and save new?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete & Overwrite",
        cancelButtonText: "Cancel",
      });

      if (!res.isConfirmed) {
        Swal.fire("Cancelled", "Old content kept. Save aborted.", "info");
        return;
      }

      try {
        setIsSaving(true);

        const del = await fetch(
          `${BlogContent_API}/delete/${existingSavedEntry.id}`,
          {
            method: "DELETE",
            headers: {
              Authorization:
                "Bearer " + (userData ? userData.access_token : ""),
              "Content-Type": "application/json",
            },
          }
        );

        if (!del.ok) {
          const txt = await del.text();
          throw new Error(txt || "Delete failed");
        }

        setExistingSavedEntry(null);
        Swal.fire(
          "Deleted",
          "Old chapter removed. Proceeding to save new.",
          "success"
        );
      } catch (err) {
        console.error("Delete before save failed:", err);
        Swal.fire(
          "Error",
          "Could not delete existing chapter. Save aborted.",
          "error"
        );
        setIsSaving(false);
        return;
      }
    }

    // -------------------------
    // Original save logic below
    // -------------------------

    setIsSaving(true);

    try {
      const images_paths = [];
      const video_links = [];

      Object.keys(localTemplateSchema).forEach((fieldKey) => {
        const def = localTemplateSchema[fieldKey];
        const val = formData[fieldKey];

        if (
          Array.isArray(def) &&
          def.length > 0 &&
          typeof def[0] === "object"
        ) {
          (val || []).forEach((item) => {
            Object.keys(item).forEach((key) => {
              const lower = key.toLowerCase();

              if (lower.includes("img") || lower.includes("image")) {
                if (item[key]) {
                  images_paths.push({
                    name: key,
                    path: item[key],
                  });
                }
              }

              if (lower.includes("video")) {
                if (item[key]) {
                  video_links.push({
                    url: item[key],
                    caption: item.videoTitle || item.title || "",
                  });
                }
              }
            });
          });
        }
      });

      const rawName = localTemplateObj
        ? localTemplateObj.template_name
        : "Template";
      const cleanName = sanitizeTemplateName(rawName);
      const chapterSafe = chapterName.trim();

      function generateJSXFromSchema(schema, formData, cleanName) {
        let propsString = "";

        Object.keys(schema).forEach((key) => {
          const val = formData[key];
          if (val === undefined || val === null) return;

          if (typeof val === "string") {
            propsString += `${key}="${val.replace(/"/g, '\\"')}" `;
          } else {
            propsString += `${key}={${JSON.stringify(val)}} `;
          }
        });

        return `<${cleanName} ${propsString.trim()} />`;
      }

      const code = generateJSXFromSchema(
        localTemplateSchema,
        formData,
        cleanName
      );

      const payload = {
        blog_id: editBlog.blog_id,
        template_id: parseInt(selectedTemplate),
        chapter_name: chapterSafe,
        images_paths,
        video_links,
        code,
        created_at: new Date().toISOString(),
      };

      const resp = await fetch(BlogContent_API + "/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + (userData ? userData.access_token : ""),
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(errText || "Failed to save chapter");
      }

      const saved = await resp.json();
      setIsSaved(true);

      Swal.fire("Saved", "Chapter saved successfully", "success");

      if (typeof onSaved === "function") {
        onSaved(saved);
      }
    } catch (err) {
      console.error("Save chapter error:", err);
      Swal.fire("Error", "Failed to save chapter. See console.", "error");
    } finally {
      setIsSaving(false);
    }
  }

  // new function for rendering contentData array items of multitab template
  function renderTemplateNewContentData(fieldKey, item, idx) {
    return (
      <div className="bce-array-object-box">
        <div className="bce-array-object-header">
          <span>
            {fieldKey} #{idx + 1}
          </span>
          <button
            className="bce-remove-btn"
            onClick={() => removeArrayItem(fieldKey, idx)}
          >
            ✕
          </button>
        </div>

        {/* TYPE DROPDOWN */}
        <div className="bce-sub-field-box">
          <label>type</label>
          <select
            className="bce-select"
            value={item.type || "image"}
            onChange={(e) =>
              updateArrayItem(fieldKey, idx, "type", e.target.value)
            }
          >
            <option value="image">Image</option>
            <option value="video">Video</option>
          </select>
        </div>

        {/* COMMON FIELDS */}
        <div className="bce-sub-field-box">
          <label>title </label>
          <input
            className="bce-input-tab"
            type="text"
            value={item.title || ""}
            onChange={(e) =>
              updateArrayItem(fieldKey, idx, "title", e.target.value)
            }
          />
        </div>

        <div className="bce-sub-field-box">
          <label>Description </label>
          <input
            className="bce-input-tab"
            type="text"
            value={item.text || ""}
            onChange={(e) =>
              updateArrayItem(fieldKey, idx, "text", e.target.value)
            }
          />
        </div>

        {/* IMAGE TYPE FIELDS */}
        {item.type === "image" && (
          <>
            <div className="bce-sub-field-box">
              <label>Upload Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const path = await uploadImageFile(file);
                  if (path) updateArrayItem(fieldKey, idx, "frameUrl", path);
                }}
              />
            </div>

            {item.frameUrl && <p className="bce-file-name">{item.frameUrl}</p>}
          </>
        )}

        {/* VIDEO TYPE FIELDS */}
        {item.type === "video" && (
          <div className="bce-sub-field-box">
            <label>Video URL</label>
            <input
              className="bce-input-tab"
              type="text"
              value={item.frameUrl || ""}
              onChange={(e) =>
                updateArrayItem(fieldKey, idx, "frameUrl", e.target.value)
              }
            />
          </div>
        )}

        {/* FRAME TITLE */}
        <div className="bce-sub-field-box">
          <label>Frame Title</label>
          <input
            className="bce-input-tab"
            type="text"
            value={item.frameTitle}
            onChange={(e) =>
              updateArrayItem(fieldKey, idx, "frameTitle", e.target.value)
            }
          />
        </div>

        {/* SOURCE*/}
        <div className="bce-sub-field-box">
          <label>Source</label>
          <input
            className="bce-input-tab"
            type="text"
            value={item.source}
            onChange={(e) =>
              updateArrayItem(fieldKey, idx, "source", e.target.value)
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bce-container">
      <div className="bce-header">
        <h2 className="bce-heading">Chapter: {chapterName}</h2>
        <div className="bce-progress">{/* optional progress */}</div>
      </div>

      {/* Template list */}
      <div className="bce-field-box">
        <label className="bce-label">Choose Template</label>
        <table className="bce-template-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Preview</th>
              <th>Select</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((tpl) => (
              <tr
                key={tpl.template_id}
                className={
                  String(selectedTemplate) === String(tpl.template_id)
                    ? "selected"
                    : ""
                }
              >
                <td>{tpl.template_name}</td>
                <td>{tpl.template_description}</td>
                <td>
                  {tpl.template_preview ? (
                    <button
                      className="bce-preview-btn"
                      onClick={() => setShowPreviewUrl(tpl.template_preview)}
                      title="Preview"
                    >
                      <MdPreview />
                    </button>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <button
                    className="bce-add-btn"
                    onClick={() => {
                      setSelectedTemplate(tpl.template_id);
                      // initialize arrays or strings if schema provided
                      if (tpl.template_schema) {
                        const schema = tpl.template_schema;
                        Object.keys(schema).forEach((k) => {
                          if (Array.isArray(schema[k]) && !formData[k])
                            updateField(k, []);
                          if (
                            schema[k] === "string" &&
                            formData[k] === undefined
                          )
                            updateField(k, "");
                        });
                      }
                    }}
                  >
                    Select
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Preview modal */}
      {showPreviewUrl && (
        <div
          className="bce-preview-modal"
          onClick={() => setShowPreviewUrl(null)}
        >
          <div
            className="bce-preview-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="bce-remove-btn"
              onClick={() => setShowPreviewUrl(null)}
            >
              Close
            </button>
            <img
              src={showPreviewUrl}
              alt="Template preview"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </div>
        </div>
      )}

      {/* Dynamic form */}
      {localTemplateSchema ? (
        <div className="bce-dynamic-form">
          {Object.keys(localTemplateSchema).map((fieldKey) =>
            renderFieldByType(fieldKey, localTemplateSchema[fieldKey])
          )}

          <div className="bce-actions">
            <button
              className="bce-save-btn"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Chapter"}
            </button>

            <button
              className="bce-next-btn"
              onClick={() => {
                if (!isSaved) {
                  Swal.fire(
                    "Save first",
                    "Please save this chapter before moving to next.",
                    "info"
                  );
                  return;
                }
                if (typeof props.onNext === "function") props.onNext();
              }}
            >
              Next Chapter →
            </button>
          </div>
        </div>
      ) : (
        <div className="bce-note">
          Select a template to start editing content for this chapter.
        </div>
      )}
    </div>
  );
}
